import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { formatDistance } from 'date-fns';
import { DateTime } from 'luxon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { take } from 'rxjs';
import { GetAnswerRequest } from '../../../../../../backend/src/core/openai/openai.gate';
import { AiIsLoadingComponent } from '../../../basic/ai/ai-is-loading/ai-is-loading.component';
import { MarkdownItPipe } from '../../../basic/pipes/markdown-it.pipe';
import { TruncatePipe } from '../../../basic/pipes/truncate.pipe';
import { Board } from '../../../core/board/board';
import { BoardFuncs } from '../../../core/board/board.func';
import { BoardHolderService } from '../../../core/board/board.holder.service';
import { BoardService } from '../../../core/board/board.service';
import { ObjectionService } from '../../../core/objection/objection.service';
import { OpenaiModel } from '../../../core/openai/openai';
import { OpenaiService } from '../../../core/openai/openai.service';
import { getImprovedResponsePrompt, getSuggesionsPrompt } from '../../../core/prompt/evaluation/get-ai-feedback.prompt';
import { getOneConversationPrompt } from '../../../core/prompt/shared/shared.prompt';
import { SessionMessage, SessionMessageSalesAiFeedback } from '../../../core/session-message/session-message';
import { SessionMessageService } from '../../../core/session-message/session-message.service';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { Session } from '../../../core/session/session';
import { BoardIconComponent } from '../../../domain/board/board-icon/board-icon.component';
@Component({
  selector: 'app-feedback-improvements',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzPopoverModule,
    NzToolTipModule,
    NzTabsModule,
    NzSkeletonModule,
    NzCommentModule,
    NzFormModule,

    AiIsLoadingComponent,
    BoardIconComponent,
    MarkdownItPipe,

    TruncatePipe,
  ],
  templateUrl: './feedback-improvements.component.html',
  styleUrl: './feedback-improvements.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackImprovementsComponent {
  @Input({ required: true }) session!: Session;

  @Input({ required: true }) index!: number;

  @Input({ required: true }) range!: {
    startTime: DateTime<boolean>;
    endTime: DateTime<boolean>;
    message: string;
    sessionMessage: SessionMessage;
  };

  @Input({ required: true }) sessionMessageList: SessionMessage[] = [];

  boardList = toSignal(this.boardService.getAllBoardList$(), { initialValue: [] });

  sessionTemplateList = toSignal(this.sessionTemplateService.itemList$(), { initialValue: [] });

  sessionMessageChanged = toSignal(this.sessionMessageService.changed$, { initialValue: undefined });

  isLoading = signal<boolean>(false);

  isLoadingSuggestions = signal<boolean>(false);
  isLoadingImprovedResponse = signal<boolean>(false);

  aiFeedback = signal<SessionMessageSalesAiFeedback | undefined>(undefined);

  constructor(
    private objectionService: ObjectionService,
    private messageService: NzMessageService,
    private boardService: BoardService,
    private openaiService: OpenaiService,
    private boardHolderService: BoardHolderService,
    private sessionTemplateService: SessionTemplateService,
    private sessionMessageService: SessionMessageService
  ) {}

  ngOnInit() {
    const currentMessage = this.sessionMessageList[this.index];
    this.aiFeedback.set(currentMessage.subData.salesAiFeedback);
  }

  myPersonalBoard = this.boardHolderService.myPersonalBoard;

  feedbackInput?: string;
  onFeedbackSubmit() {
    if (!this.feedbackInput) {
      this.messageService.error('Please enter feedback to proceed.');
      return;
    }
    this.isLoading.set(true);
    this.sessionMessageService
      .addFeedback$(this.range.sessionMessage.id, this.feedbackInput!)
      .pipe(take(1))
      .subscribe((response) => {
        if (response) {
          this.range.sessionMessage = response;
          this.feedbackInput = '';
          this.isLoading.set(false);
        }
      });
  }

  onRegenerateAiImprovementsClick() {
    this.getAiFeedback(true);
  }

  getAiFeedback(refresh = false) {
    this.isLoading.set(true);

    const currentMessage = this.sessionMessageList[this.index];

    if (currentMessage.subData.salesAiFeedback && !refresh) {
      this.aiFeedback.set(currentMessage.subData.salesAiFeedback);
    } else {
      const goalDescription = this.session.subData.goal?.description!;
      const list = [...this.sessionMessageList].slice(0, this.index);

      const previousConversationList = list.map((c) =>
        getOneConversationPrompt(c.subData.salesMessage, c.subData.customerMessage)
      );

      const requestPrompt = {
        goalDescription,
        product: this.session.subData.product,
        methodology: this.session.subData.methodology!,
        salesResponse: currentMessage.subData.salesMessage,
        previousConversationList,
        scenarioInstruction: this.session.subData.template?.unsafeSubData?.instruction,
      };

      const suggestionsPrompt = getSuggesionsPrompt(requestPrompt);
      const suggesionsRequest: GetAnswerRequest = {
        messageList: [{ role: 'user', content: suggestionsPrompt }],
        config: {
          openaiModel: OpenaiModel.o1Mini,
        },
      };

      this.isLoadingSuggestions.set(true);

      this.openaiService
        .getAnswer$(suggesionsRequest)
        .pipe(take(1))
        .subscribe((result) => {
          this.isLoadingSuggestions.set(false);

          if (!currentMessage.subData.salesAiFeedback || typeof currentMessage.subData.salesAiFeedback === 'string') {
            currentMessage.subData.salesAiFeedback = {
              rewrittenResponse: '',
              improvementSuggestions: [],
            };
          }

          currentMessage.subData.salesAiFeedback.improvementSuggestions = [result];

          this.sessionMessageService.updateAiFeedback$(currentMessage).pipe(take(1)).subscribe();

          this.aiFeedback.set(currentMessage.subData.salesAiFeedback);
        });

      this.isLoadingImprovedResponse.set(true);

      const improvePrompt = getImprovedResponsePrompt(requestPrompt);
      const improveRequest: GetAnswerRequest = {
        messageList: [{ role: 'user', content: improvePrompt }],
        config: {
          openaiModel: OpenaiModel.o1Mini,
        },
      };

      this.openaiService
        .getAnswer$(improveRequest)
        .pipe(take(1))
        .subscribe((result) => {
          this.isLoadingImprovedResponse.set(false);

          if (!currentMessage.subData.salesAiFeedback || typeof currentMessage.subData.salesAiFeedback === 'string') {
            currentMessage.subData.salesAiFeedback = {
              rewrittenResponse: '',
              improvementSuggestions: [],
            };
          }

          currentMessage.subData.salesAiFeedback.rewrittenResponse = result;

          this.sessionMessageService.updateAiFeedback$(currentMessage).pipe(take(1)).subscribe();

          this.aiFeedback.set(currentMessage.subData.salesAiFeedback);
        });
    }
  }

  getCommentsDiff(timeStr?: string) {
    if (!timeStr) {
      return '';
    }

    const now = DateTime.local();

    return formatDistance(DateTime.fromISO(timeStr).toJSDate(), now.toJSDate());
  }

  getBoard(id: string) {
    return this.boardList().find((c) => c.id === id);
  }

  getBoardName(board?: Board) {
    return BoardFuncs.getName(board);
  }
}
